/**
 * Blossom — header.js
 * <header-component>
 *  - Keeps layout metrics in CSS variables on :root for other sticky UI:
 *      --header-height, --announcement-height, --announcement-sticky-height,
 *      --header-offset (height of everything currently stuck to the top)
 *  - Sticky modes: none | always | scroll-up (hides while scrolling down)
 *  - Transparent → solid state (.is-scrolled) on the home page
 *  - Mega menu / dropdown disclosure: hover intent (mouse), click/Enter/Space
 *    on the toggle, Esc closes and restores focus, closes on focus-out and
 *    outside click. No functionality is hover-only.
 */
(() => {
  if (!window.Theme || customElements.get('header-component')) return;

  const root = document.documentElement;
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 220;

  class HeaderComponent extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.section = this.closest('.shopify-section') || this;
      this.sticky = this.dataset.sticky || 'none';
      this.lastScrollY = window.scrollY;
      this.menuItems = Array.from(this.querySelectorAll('[data-menu-item]'));

      this.bindMenus(signal);
      this.observeSizes();

      window.addEventListener('scroll', () => this.requestScrollUpdate(), { passive: true, signal });
      window.addEventListener('resize', Theme.debounce(() => this.updateMetrics(), 150), { signal });
      document.addEventListener(
        'click',
        (event) => {
          if (this.openItem && !this.contains(event.target)) this.closeAll();
        },
        { signal }
      );
      desktopQuery.addEventListener('change', () => this.closeAll(), { signal });
      this.unsubscribeDrawer = Theme.on(Theme.events.DRAWER_OPEN, () => this.closeAll());

      if (Theme.designMode) this.bindEditor(signal);

      this.updateMetrics();
      this.onScroll();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      this.unsubscribeDrawer?.();
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.section?.classList.remove('header-section--hidden');
    }

    /* ------------------------------------------------------------ Metrics */
    get announcements() {
      // Only bars placed above the header affect its sticky offset.
      return Array.from(document.querySelectorAll('announcement-bar')).filter(
        (bar) => bar.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING
      );
    }

    observeSizes() {
      this.resizeObserver = new ResizeObserver(() => this.updateMetrics());
      this.resizeObserver.observe(this);
      this.announcements.forEach((bar) => this.resizeObserver.observe(bar));
    }

    updateMetrics() {
      const headerHeight = Math.round(this.getBoundingClientRect().height);
      let announcementHeight = 0;
      let announcementSticky = 0;
      this.announcements.forEach((bar) => {
        const height = bar.hidden ? 0 : Math.round(bar.getBoundingClientRect().height);
        announcementHeight += height;
        if (bar.dataset.sticky === 'true') announcementSticky += height;
      });

      this.headerHeight = headerHeight;
      this.announcementHeight = announcementHeight;
      this.announcementSticky = announcementSticky;

      root.style.setProperty('--header-height', `${headerHeight}px`);
      root.style.setProperty('--announcement-height', `${announcementHeight}px`);
      root.style.setProperty('--announcement-sticky-height', `${announcementSticky}px`);
      this.updateOffset();
    }

    updateOffset() {
      const headerVisible = this.sticky !== 'none' && !this.section.classList.contains('header-section--hidden');
      const offset = (this.announcementSticky || 0) + (headerVisible ? this.headerHeight || 0 : 0);
      root.style.setProperty('--header-offset', `${offset}px`);
    }

    /* ------------------------------------------------------------- Scroll */
    requestScrollUpdate() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.onScroll();
        this.ticking = false;
      });
    }

    onScroll() {
      const y = Math.max(window.scrollY, 0);
      const stuckAt = Math.max((this.announcementHeight || 0) - (this.announcementSticky || 0), 0);

      if (this.sticky !== 'none') this.classList.toggle('is-scrolled', y > stuckAt + 1);

      if (this.sticky === 'scroll-up') {
        const delta = y - this.lastScrollY;
        const pastHeader = y > stuckAt + (this.headerHeight || 0) * 1.5;
        if (!pastHeader) {
          this.setHidden(false);
        } else if (delta > 6 && !this.openItem) {
          this.setHidden(true);
        } else if (delta < -6) {
          this.setHidden(false);
        }
      }

      this.lastScrollY = y;
    }

    setHidden(hidden) {
      if (this.section.classList.contains('header-section--hidden') === hidden) return;
      this.section.classList.toggle('header-section--hidden', hidden);
      if (hidden) this.closeAll();
      this.updateOffset();
    }

    /* -------------------------------------------------------------- Menus */
    bindMenus(signal) {
      this.menuItems.forEach((item) => {
        const toggle = item.querySelector(':scope > [data-menu-toggle]');
        const panel = item.querySelector(':scope > [data-menu-panel]');
        if (!toggle || !panel) return;

        toggle.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            if (this.openItem === item) this.close(item);
            else this.open(item);
          },
          { signal }
        );

        item.addEventListener(
          'pointerenter',
          (event) => {
            if (event.pointerType !== 'mouse' || !desktopQuery.matches) return;
            clearTimeout(this.closeTimer);
            clearTimeout(this.openTimer);
            // Switch instantly when moving between menus, otherwise wait briefly (hover intent).
            const delay = this.openItem && this.openItem !== item ? 0 : OPEN_DELAY;
            this.openTimer = setTimeout(() => this.open(item), delay);
          },
          { signal }
        );

        item.addEventListener(
          'pointerleave',
          (event) => {
            if (event.pointerType !== 'mouse') return;
            clearTimeout(this.openTimer);
            this.closeTimer = setTimeout(() => this.close(item), CLOSE_DELAY);
          },
          { signal }
        );

        item.addEventListener(
          'focusout',
          (event) => {
            if (!item.contains(event.relatedTarget)) this.close(item);
          },
          { signal }
        );

        item.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'Escape' || this.openItem !== item) return;
            event.preventDefault();
            event.stopPropagation();
            this.close(item);
            toggle.focus();
          },
          { signal }
        );
      });
    }

    open(item) {
      if (this.openItem === item) return;
      if (this.openItem) this.close(this.openItem);
      const toggle = item.querySelector(':scope > [data-menu-toggle]');
      item.classList.add('is-open');
      toggle?.setAttribute('aria-expanded', 'true');
      this.openItem = item;
      this.classList.add('is-menu-open');
    }

    close(item) {
      if (!item) return;
      const toggle = item.querySelector(':scope > [data-menu-toggle]');
      item.classList.remove('is-open');
      toggle?.setAttribute('aria-expanded', 'false');
      if (this.openItem === item) {
        this.openItem = null;
        this.classList.remove('is-menu-open');
      }
    }

    closeAll() {
      clearTimeout(this.openTimer);
      this.menuItems.forEach((item) => this.close(item));
    }

    /* ------------------------------------------------------ Theme editor */
    bindEditor(signal) {
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          if (!this.contains(event.target)) return;
          const item = event.target.closest('[data-menu-item]');
          if (item) this.open(item);
        },
        { signal }
      );
      document.addEventListener(
        'shopify:block:deselect',
        (event) => {
          if (this.contains(event.target)) this.closeAll();
        },
        { signal }
      );
      // Announcement bars re-rendered by the editor need to be observed again.
      document.addEventListener(
        'shopify:section:load',
        () => {
          this.resizeObserver?.disconnect();
          this.observeSizes();
          this.updateMetrics();
        },
        { signal }
      );
    }
  }

  customElements.define('header-component', HeaderComponent);
})();
